import {
  numbersTypography,
  primaryTypography,
} from 'styles/global/typography/typography';
import colors from 'styles/themes/colors';
import styled, { css } from 'styled-components/macro';
import Button from 'components/elements/button';
import { rgba } from 'polished';

export const DateRangeWrapper = styled('div')<{
  isSmallScreen: boolean;
  margins: any;
}>`
  width: ${({ isSmallScreen }): any => !isSmallScreen && '290px !important'};
  box-shadow: 0px 4px 4px rgb(164 164 164 / 25%);
  border: 1px solid #e5e5e5;
  background-color: ${colors.White};
  position: absolute;
  top: ${({ margins }): string =>
    margins && margins.top ? `${margins.top}px` : '52px'};
  z-index: 10;
  right: 0;
  margin: 0 !important;

  ${({ isSmallScreen }): any =>
    isSmallScreen &&
    css`
      padding: ${({ theme }): string => theme.spacing.small};
      height: 100vh;
      z-index: 99999;
      background-color: ${({ theme }): string => rgba(theme.NightBlue, 0.85)};
      position: fixed;
      top: 0;
      left: 0;
      bottom: 0;
      right: 0;
      overflow-y: hidden;
      overflow: auto;
      overscroll-behavior-y: contain;
      -webkit-overflow-scrolling: touch;
    `}
`;

export const DateRangeContent = styled('div')<{ isSmallScreen: boolean }>`
  ${({ isSmallScreen }): any =>
    isSmallScreen &&
    css`
      width: 100%;
      height: 100%;
      background: ${colors.White};

      ${DateRangeContainer} {
        height: calc(100vh - 158px);
      }
    `}
`;

export const CalendarWrapper = styled('div')`
  overflow: hidden;
  height: 100%;
`;

export const DateRangeContainer = styled('div')`
  height: 405px;
`;

export const DayElement = styled('div')<any>`
  font-size: 11px;
  font-weight: 600;
  ${numbersTypography}
  letter-spacing: 0.42px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  height: 32px;
  text-align: center;
  width: 38.6px;
  padding: 0 3px;
  margin: 4px 0;
  vertical-align: top;
  position: relative;
  border-radius: 50%;
  outline: none;

  @media screen and (max-width: 40em) {
    width: calc(100% / 7);
    max-width: calc(100% / 7);
  }

  .text-day {
    position: relative;
    z-index: 3;
  }

  ${({ selected }): any =>
    selected &&
    css`
      color: white;

      &::after {
        background-color: ${colors.AccentGreen};
        border-radius: 100%;
      }
    `}

  ${({ hovered }): any =>
    hovered &&
    css`
        {
        background-color: ${colors.Sand};
        border-radius: unset;

        ${DayBackground} {
          opacity: 1;
        }

        &.end {
          background: linear-gradient(90deg, ${colors.Sand} 50%, ${colors.White} 50%); 
        }

        &:hover {
          color: ${colors.White}

          &::after {
            border-radius: 100%;
            background-color: ${colors.White};
          }
        }
      }
    `}

  ${({ hovered, selected }): any =>
    hovered &&
    selected &&
    css`
       {
        background: linear-gradient(
          90deg,
          ${colors.White} 50%,
          ${colors.Sand} 50%
        );
        color: ${colors.White};

        &.end {
          background: linear-gradient(
            90deg,
            ${colors.Sand} 50%,
            ${colors.White} 50%
          );
        }

        &:hover {
          color: ${colors.White};
        }

        &::after {
          border-radius: 100%;
          background-color: ${colors.AccentGreen};
        }

        &:hover::after {
          background-color: ${colors.White};
        }
      }
    `}

  ${({ highlight }): any =>
    highlight &&
    css`
      &::before {
        content: '';
        width: 32px;
        height: 32px;
        position: absolute;
        left: 50%;
        top: 50%;
        transform: translate(-50%, -50%);
        border-radius: 50%;
        border: 1px solid ${colors.AccentGreen};
      }
    `}

  ${({ disabled }): any =>
    disabled &&
    css`
      pointer-events: none;
      color: ${colors.DisabledGrey};
    `}

  &::after {
    border-radius: 100%;
    bottom: 0;
    content: '';
    height: 32px;
    left: 0;
    margin: auto;
    position: absolute;
    right: 0;
    top: 0;
    width: 32px;
    z-index: 2;
  }

  &:hover {
    color: ${colors.White};
  }

  &:hover::after {
    background-color: ${colors.AccentGreen};
    color: ${colors.White};
  }
`;

export const DayBackground = styled('div')<{
  isFirstDay?: boolean;
  isLastDay?: boolean;
}>`
  bottom: 0;
  opacity: 0;
  pointer-events: none;
  position: absolute;
  top: 0;
  width: 10px;
  z-index: 1;

  ${({ isFirstDay }): any =>
    isFirstDay &&
    css`
      background: linear-gradient(to right, ${colors.White}, ${colors.Sand});
      left: -10px;
    `}

  ${({ isLastDay }): any =>
    isLastDay &&
    css`
      background: linear-gradient(to left, ${colors.White}, ${colors.Sand});
      right: -10px;
    `}
`;

export const WeekElement = styled('div')<{ isFirst: boolean }>`
  display: flex;
  justify-content: ${({ isFirst }): string =>
    isFirst ? 'flex-end' : 'flex-start'};
`;

export const CalendarContent = styled('div')`
  width: auto;
  display: flex;
  padding: 0 0 0 10px;
  position: relative;
  overflow: hidden;
  height: 100%;
`;

export const MonthWrapper = styled('div')`
  padding-right: 10px;
`;

export const MonthName = styled('div')`
  ${primaryTypography}
  font-size: 16px;
  font-weight: 600;
  color: ${colors.DeepBlue};
  text-align: center;
  margin-bottom: 10px;
`;

// Header styles
export const HeaderWrapper = styled('div')`
  display: flex;
  flex-direction: row;
  justify-content: space-around;
  padding: ${({ theme }): string =>
    `${theme.spacing.medium} ${theme.spacing.small}`};
`;

export const DateElement = styled('div')`
  display: flex;
  flex-direction: column;
  min-width: 96.55px;
`;

export const Label = styled('span')`
  ${primaryTypography}
  font-size: 11px;
  font-weight: 600;
  color: ${colors.CasperGrey};
  text-transform: uppercase;
  letter-spacing: normal;
`;

export const Value = styled('span')`
  ${numbersTypography}
  color: ${colors.DeepBlue};
  font-size: 16px;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    left: 0;
    bottom: 0;
    height: 2px;
    width: 100%;
    background: ${colors.AccentGreen};
  }
`;

// Footer styles
export const FooterWrapper = styled('div')<{ isMobile: boolean }>`
  display: flex;
  flex-direction: ${({ isMobile }): string =>
    isMobile ? 'row-reverse' : 'row'};
  justify-content: space-between;
  align-items: center;
  height: 64px;
  padding: 0 1em;
`;

export const FooterButton = styled(Button)`
  font-weight: 600;
  font-size: 14px;
  line-height: 17px;
  height: 32px;
  padding: ${({ theme }): string => `0 ${theme.spacing.small}`};
  width: 50%;
  border-radius: 3px;
`;

export const Line = styled.div`
  width: 100%;
  height: 1px;
  background: #e5e5e5;
`;

const textStyles = css`
  font-size: 13px;
  line-height: 16px;
  color: ${colors.CasperGrey};
  font-weight: 500;
`;

export const TopHeaderText = styled('span')`
  ${textStyles};
  display: flex;
  justify-content: center;
  padding: 1.21em 0;
`;

export const ButtonContainer = styled.div`
  padding: 0 1em;

  label {
    padding: 0.21em 0;
    font-size: 14px;
    width: 50%;
  }
`;

export const Circle = styled.div`
  width: 17px;
  height: 17px;
  border-radius: 8px;
  border: 1px solid #0bd6a7;
  margin-right: 0.3em;
`;

export const InfoText = styled.span`
  ${textStyles};
`;
